import { useState } from 'react';
import { Container, Typography, Box, Alert, Snackbar } from '@mui/material';
import BasePage from '../BasePage';
import AdminLayout from '../../components/AdminLayout';
import BusinessFilters from '../../components/admin/businesses/BusinessFilters';
import BusinessesTable from '../../components/admin/businesses/BusinessesTable';
import BusinessDetailModal from '../../components/admin/businesses/BusinessDetailModal';
import { useAdminBusinesses } from '../../hooks/admin/useAdminBusinesses';
import { AdminBusiness } from '../../types/admin';

/**
 * AdminBusinesses - Business management page
 *
 * Section 3: Businesses
 * - Business list table with search and filters
 * - Business detail modal with tabs (Overview, Agents, Clients, Analytics)
 * - Activate/deactivate business functionality
 * - Comprehensive business metrics and analytics
 *
 * Features:
 * - Search by business name, owner name, or email
 * - Filter by status (active/inactive) and size (small/medium/large)
 * - Pagination (50 businesses per page)
 * - Business detail drawer with 4 tabs
 * - Agent and client hierarchical data display
 * - Performance analytics and charts
 * - Toast notifications for actions
 */
export default function AdminBusinesses() {
  const {
    businesses,
    totalBusinesses,
    isLoading,
    error,
    pagination,
    filters,
    searchBusinesses,
    filterBusinesses,
    clearFilters,
    setPagination,
    activateBusiness,
    deactivateBusiness,
    getBusinessDetail,
    selectedBusinessId,
    setSelectedBusinessId,
  } = useAdminBusinesses();

  // Toast notification state
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Handlers for table actions
  const handleViewBusiness = (business: AdminBusiness) => {
    setSelectedBusinessId(business.id);
  };

  const handleActivateBusiness = async (business: AdminBusiness) => {
    const result = await activateBusiness(business.id);
    if (result.success) {
      setToast({
        open: true,
        message: `Business "${business.name}" has been activated successfully`,
        severity: 'success',
      });
      // Refresh selected business detail if open
      if (selectedBusinessId === business.id) {
        setSelectedBusinessId(null);
        setTimeout(() => setSelectedBusinessId(business.id), 100);
      }
    } else {
      setToast({
        open: true,
        message: result.error || 'Failed to activate business',
        severity: 'error',
      });
    }
  };

  const handleDeactivateBusiness = async (business: AdminBusiness) => {
    const result = await deactivateBusiness(business.id);
    if (result.success) {
      setToast({
        open: true,
        message: `Business "${business.name}" has been deactivated`,
        severity: 'success',
      });
      // Refresh selected business detail if open
      if (selectedBusinessId === business.id) {
        setSelectedBusinessId(null);
        setTimeout(() => setSelectedBusinessId(business.id), 100);
      }
    } else {
      setToast({
        open: true,
        message: result.error || 'Failed to deactivate business',
        severity: 'error',
      });
    }
  };

  // Detail drawer moderation actions
  const handleDetailActivate = async () => {
    if (!selectedBusinessId) return;
    const business = businesses.find((b) => b.id === selectedBusinessId);
    if (business) {
      await handleActivateBusiness(business);
    }
  };

  const handleDetailDeactivate = async () => {
    if (!selectedBusinessId) return;
    const business = businesses.find((b) => b.id === selectedBusinessId);
    if (business) {
      await handleDeactivateBusiness(business);
    }
  };

  return (
    <BasePage
      title="Business Management | CribCritic Admin"
      description="Manage businesses, agents, and clients"
    >
      <AdminLayout>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Business Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage businesses, view agents, and track performance • {totalBusinesses.toLocaleString()} businesses found
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <BusinessFilters
            filters={filters}
            onSearchChange={searchBusinesses}
            onFilterChange={filterBusinesses}
            onClearFilters={clearFilters}
          />

          <BusinessesTable
            businesses={businesses}
            isLoading={isLoading}
            error={error}
            pagination={pagination}
            onPageChange={(page) => setPagination({ ...pagination, page })}
            onLimitChange={(limit) => setPagination({ ...pagination, limit, page: 1 })}
            onViewBusiness={handleViewBusiness}
            onActivateBusiness={handleActivateBusiness}
            onDeactivateBusiness={handleDeactivateBusiness}
          />

          {/* Business Detail Drawer */}
          <BusinessDetailModal
            open={!!selectedBusinessId}
            businessId={selectedBusinessId}
            getBusinessDetail={getBusinessDetail}
            onClose={() => setSelectedBusinessId(null)}
            onActivateBusiness={handleDetailActivate}
            onDeactivateBusiness={handleDetailDeactivate}
          />

          {/* Toast Notifications */}
          <Snackbar
            open={toast.open}
            autoHideDuration={6000}
            onClose={() => setToast({ ...toast, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setToast({ ...toast, open: false })}
              severity={toast.severity}
              sx={{ width: '100%' }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Container>
      </AdminLayout>
    </BasePage>
  );
}
